import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { TextareaInput, TextInput } from "@/components/ui/Field";
import { NativeSelectField } from "@/components/ui/NativeSelect";
import { Spinner } from "@/components/ui/Spinner";
import { LieferscheinBillingTab } from "@/features/timesheetFlow/components/LieferscheinBillingTab";
import { TimesheetActionLogList } from "@/features/timesheetFlow/components/TimesheetActionLogList";
import { TimesheetDetailFooter } from "@/features/timesheetFlow/components/TimesheetDetailFooter";
import { TimesheetFlowCard } from "@/features/timesheetFlow/components/TimesheetFlowCard";
import { TimesheetPdfEmbed } from "@/features/timesheetFlow/components/TimesheetPdfEmbed";
import "@/features/timesheetFlow/timesheetFlow.css";
import {
  fetchLieferscheinActions,
  fetchLieferscheinHistory,
  getDefaultPrintLocation,
  getLieferscheinPrintUrl,
  lieferscheinPdfApiPath,
  lieferscheinPdfUrl,
  listPrintLocations,
  runPrintByLocationWithFeedback,
  sendLieferscheinMail,
  setDefaultPrintLocation,
  type ActionLogEntry,
  type LieferscheinActionsInfo,
  type PrintLocation,
} from "@/features/timesheetFlow/timesheetFlowApi";
import { PrintJobFeedbackError } from "@/lib/printJob";
import { useCurrentUser } from "@/lib/auth";
import {
  archiveLieferschein,
  getLieferscheinDetail,
  lieferscheinBillingStatusClass,
  lieferscheinBillingStatusLabel,
  unarchiveLieferschein,
  type LieferscheinDetail,
} from "@/lib/lieferscheine";
import { TimesheetBadge } from "@/pages/stundennachweise/TimesheetBadge";
import {
  resolveTimesheetPermissions,
  type TimesheetPermissions,
} from "@/features/timesheetFlow/timesheetPermissions";

type DetailTab = "overview" | "actions" | "history" | "billing";

const TAB_LABELS: Record<DetailTab, string> = {
  overview: "Übersicht",
  actions: "Aktionen",
  history: "Historie",
  billing: "Abrechnung & Rückfragen",
};

function fmtDateDe(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("de-DE");
}

const BILLING_TAB_STATUSES = new Set([
  "signed",
  "assigned",
  "invoice_draft",
  "invoice_created",
  "geliefert",
]);

export function LieferscheinDetailPanel({
  auftragId,
  dokumentId,
  onClose,
  onRefresh,
}: {
  auftragId: number;
  dokumentId: number;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const [info, setInfo] = useState<LieferscheinActionsInfo | null>(null);
  const [billingDetail, setBillingDetail] = useState<LieferscheinDetail | null>(null);
  const [actionLog, setActionLog] = useState<ActionLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<DetailTab>("overview");
  const [permissions, setPermissions] = useState<TimesheetPermissions | null>(null);
  const [openBillingCount, setOpenBillingCount] = useState(0);
  const { user } = useCurrentUser();

  const [locations, setLocations] = useState<PrintLocation[]>([]);
  const [locationId, setLocationId] = useState("");
  const [copies, setCopies] = useState("1");
  const [printBusy, setPrintBusy] = useState(false);
  const [mailTo, setMailTo] = useState("");
  const [mailCc, setMailCc] = useState("");
  const [mailBusy, setMailBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [actions, history, detail] = await Promise.all([
        fetchLieferscheinActions(auftragId, dokumentId),
        fetchLieferscheinHistory(auftragId, dokumentId).catch(() => []),
        getLieferscheinDetail(dokumentId).catch(() => null),
      ]);
      setInfo(actions);
      setBillingDetail(detail);
      setMailTo((actions.recipients || []).join(", "));
      setMailCc((actions.cc || []).join(", "));
      setActionLog(history);
    } catch (err: unknown) {
      setInfo(null);
      setBillingDetail(null);
      setError(err instanceof Error ? err.message : "Lieferschein konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [auftragId, dokumentId]);

  useEffect(() => {
    setTab("overview");
    void reload();
  }, [reload]);

  useEffect(() => {
    let cancelled = false;
    void resolveTimesheetPermissions(user).then((next) => {
      if (!cancelled) setPermissions(next);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const billingStatus = String(billingDetail?.billing_status || info?.status || "")
    .trim()
    .toLowerCase();
  const archived =
    Boolean(billingDetail?.billing_archived_at) || billingStatus === "archived";
  const showBillingTab =
    Boolean(permissions?.canViewBillingTab) &&
    !archived &&
    BILLING_TAB_STATUSES.has(billingStatus);

  useEffect(() => {
    if (tab === "billing" && !showBillingTab) setTab("overview");
    if (tab === "actions" && permissions && !permissions.canManage) setTab("overview");
  }, [tab, showBillingTab, permissions]);

  useEffect(() => {
    void (async () => {
      try {
        const [locs, def] = await Promise.all([
          listPrintLocations(),
          getDefaultPrintLocation().catch(() => null),
        ]);
        setLocations(locs);
        if (def?.default_location_id) {
          setLocationId(String(def.default_location_id));
        } else if (locs[0]?.id) {
          setLocationId(String(locs[0].id));
        }
      } catch {
        setLocations([]);
      }
    })();
  }, []);

  const displayNumber = info?.dokument_nummer || billingDetail?.display_number || String(dokumentId);
  const headerDate = fmtDateDe(
    billingDetail?.lieferdatum || billingDetail?.belegdatum || info?.lieferdatum || info?.created_at,
  );
  const metaParts = [
    info?.kunde_name,
    info?.ansprechpartner_name ? `AP: ${info.ansprechpartner_name}` : null,
    info?.betreff,
  ].filter(Boolean);
  const busy = printBusy || mailBusy;

  const handlePrint = async () => {
    const loc = Number(locationId);
    const numCopies = Math.max(1, Number(copies) || 1);
    if (!loc) {
      setError("Bitte einen Standort auswählen.");
      return;
    }
    setPrintBusy(true);
    setError(null);
    try {
      await setDefaultPrintLocation(loc);
      const { url } = await getLieferscheinPrintUrl(auftragId, dokumentId);
      await runPrintByLocationWithFeedback(loc, numCopies, url, {
        loadingMessage: "Lieferschein wird gedruckt…",
      });
      onRefresh();
    } catch (err: unknown) {
      if (err instanceof PrintJobFeedbackError) {
        setError(err.feedback.message || err.feedback.title || "Druck fehlgeschlagen.");
      } else {
        setError(err instanceof Error ? err.message : "Druck fehlgeschlagen.");
      }
    } finally {
      setPrintBusy(false);
    }
  };

  const handleSendMail = async () => {
    const recipients = mailTo
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!recipients.length) {
      setError("Bitte mindestens einen Empfänger angeben.");
      return;
    }
    const cc = mailCc
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
    setMailBusy(true);
    setError(null);
    try {
      await sendLieferscheinMail(auftragId, dokumentId, {
        to: recipients.join("; "),
        cc: cc.length ? cc.join("; ") : undefined,
        subject: `Lieferschein ${displayNumber}`,
        mark_versendet: true,
      });
      await reload();
      onRefresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "E-Mail konnte nicht versendet werden.");
    } finally {
      setMailBusy(false);
    }
  };

  const showActionsTab = Boolean(permissions?.canManage);
  const tabs: DetailTab[] = [
    "overview",
    ...(showActionsTab ? (["actions"] as const) : []),
    "history",
    ...(showBillingTab ? (["billing"] as const) : []),
  ];

  if (loading && !info) {
    return (
      <div className="flex min-h-[16rem] flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-separator px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-semibold text-label">Lieferschein</span>
          <span className="text-sm font-semibold tabular-nums text-label">{displayNumber}</span>
          <TimesheetBadge className={lieferscheinBillingStatusClass(billingStatus)}>
            {lieferscheinBillingStatusLabel(billingStatus)}
          </TimesheetBadge>
          {headerDate ? <span className="text-sm text-label-secondary">{headerDate}</span> : null}
        </div>
        {metaParts.length ? (
          <p className="mt-1 text-xs text-label-secondary">{metaParts.join(" · ")}</p>
        ) : null}
      </div>

      {error ? <p className="mx-4 mt-2 shrink-0 text-sm text-status-red">{error}</p> : null}

      <nav
        className="flex shrink-0 flex-wrap gap-1 border-b border-separator px-4 pb-0 pt-2"
        aria-label="Bereiche"
      >
        {tabs.map((key) => (
          <button
            key={key}
            type="button"
            className={clsx(
              "rounded-t-ios px-3 py-2 text-sm font-medium transition",
              tab === key
                ? "bg-card-elevated text-label ring-1 ring-separator ring-b-0"
                : "text-label-secondary hover:bg-fill-tertiary",
            )}
            onClick={() => setTab(key)}
          >
            {TAB_LABELS[key]}
            {key === "billing" && openBillingCount > 0 ? (
              <span className="ml-1.5 inline-flex min-w-[1.25rem] justify-center rounded-full bg-status-red px-1.5 py-0.5 text-[10px] font-semibold text-white">
                {openBillingCount}
              </span>
            ) : null}
          </button>
        ))}
      </nav>

      <div
        className={clsx(
          "relative flex min-h-0 flex-1 flex-col",
          tab !== "overview" && "overflow-y-auto px-4 py-3",
        )}
      >
        <div
          className={clsx("flex min-h-0 flex-1 flex-col", tab !== "overview" && "hidden")}
          aria-hidden={tab !== "overview"}
        >
          <TimesheetPdfEmbed
            timesheetId={dokumentId}
            isSigned
            apiPath={lieferscheinPdfApiPath(auftragId, dokumentId)}
            title="Lieferschein PDF"
            bare
            className="min-h-0 flex-1"
          />
        </div>

        {tab === "actions" ? (
          <div className="space-y-3">
            <p className="text-xs text-label-secondary">
              Primär zuerst: PDF in der Übersicht prüfen, dann Versand oder Druck ausführen.
            </p>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <TimesheetFlowCard title="Drucken">
                <div className="eh-po-print-grid">
                  <NativeSelectField
                    label="Standort"
                    selectSize="sm"
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                  >
                    {locations.length === 0 ? (
                      <option value="">— Standort auswählen —</option>
                    ) : null}
                    {locations.map((loc) => (
                      <option key={loc.id} value={String(loc.id)}>
                        {loc.name || `Standort ${loc.id}`}
                      </option>
                    ))}
                  </NativeSelectField>
                  <TextInput
                    label="Kopien"
                    type="number"
                    min={1}
                    inputClassName="!w-full"
                    className="mb-0"
                    value={copies}
                    onChange={(e) => setCopies(e.target.value)}
                  />
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy || !locationId}
                    onClick={() => void handlePrint()}
                  >
                    Drucken
                  </Button>
                </div>
                <p className="mt-2 text-xs text-label-tertiary">
                  Druckt die PDF am ausgewählten Standort.
                </p>
              </TimesheetFlowCard>

              <TimesheetFlowCard title="E-Mail">
                <TextareaInput
                  label="Empfänger"
                  rows={2}
                  value={mailTo}
                  onChange={(e) => setMailTo(e.target.value)}
                  placeholder="name@firma.de, weitere@firma.de"
                />
                <TextareaInput
                  label="CC"
                  rows={2}
                  className="mt-2"
                  value={mailCc}
                  onChange={(e) => setMailCc(e.target.value)}
                  placeholder="optional"
                />
                <p className="mt-1 text-xs text-label-tertiary">
                  Mehrere Adressen mit Komma trennen. Die Team-Mail erscheint in CC, wenn sie für
                  Lieferscheine hinterlegt ist.
                </p>
                <div className="mt-3">
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy}
                    onClick={() => void handleSendMail()}
                  >
                    E-Mail senden
                  </Button>
                </div>
              </TimesheetFlowCard>
            </div>
          </div>
        ) : null}

        {tab === "billing" && showBillingTab && permissions ? (
          <div className="eh-ts-billing-tab-root flex min-h-0 w-full flex-1 flex-col">
            <LieferscheinBillingTab
              dokumentId={dokumentId}
              auftragId={auftragId}
              permissions={permissions}
              onRefresh={onRefresh}
              onError={setError}
              onOpenCountChange={setOpenBillingCount}
              onClose={onClose}
            />
          </div>
        ) : null}

        {tab === "history" ? (
          <section>
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-label">Historie</h3>
              <Button type="button" size="sm" variant="secondary" onClick={() => void reload()}>
                Aktualisieren
              </Button>
            </div>
            <TimesheetActionLogList
              entries={actionLog}
              timesheetLabel={displayNumber}
              documentKind="lieferschein"
            />
          </section>
        ) : null}
      </div>

      <TimesheetDetailFooter
        onClose={onClose}
        actions={[
          {
            id: "archive",
            label: archived ? "Wiederherstellen" : "Archivieren",
            onClick: () => {
              setMailBusy(true);
              const fn = archived ? unarchiveLieferschein : archiveLieferschein;
              void fn(dokumentId)
                .then(() => reload())
                .then(() => onRefresh())
                .catch((err: unknown) => {
                  setError(err instanceof Error ? err.message : "Archivierung fehlgeschlagen.");
                })
                .finally(() => setMailBusy(false));
            },
            hidden:
              !permissions?.canArchive ||
              (!archived && !BILLING_TAB_STATUSES.has(billingStatus)),
            disabled: busy,
          },
          {
            id: "pdf",
            label: "PDF öffnen",
            variant: "primary",
            onClick: () => {
              window.open(lieferscheinPdfUrl(auftragId, dokumentId), "_blank", "noopener");
            },
            disabled: busy,
          },
        ]}
      />
    </div>
  );
}
